import { getRiskLevel, NA_VALUE } from './constants'

/**
 * Computes inherent risk from probability and impact scores
 * Formula from Plantilla ARI+RAT addval v1.1:
 * - P = round(average of Probability factors, excluding N/A)
 * - I = max(Impact factors, excluding N/A)
 * - risk = P × I
 * - level: Bajo (1-4), Medio (5-9), Alto (10-14), Muy Alto (15-19), Crítico (20-25)
 *
 * @param {Object} scores - Scores object with probability and impact factors
 * @param {Array<number|null>} scores.probability - Probability factor scores (1-5 or null for N/A)
 * @param {Array<number|null>} scores.impact - Impact factor scores (1-5 or null for N/A)
 * @returns {Object} { P, I, risk, level } or all null if insufficient data
 */
export function computeInherent(scores) {
  if (!scores || !scores.probability || !scores.impact) {
    return { P: null, I: null, risk: null, level: null }
  }

  const { probability, impact } = scores

  // Filter out N/A values
  const validProbability = probability.filter(score => score !== NA_VALUE && !isNaN(score))
  const validImpact = impact.filter(score => score !== NA_VALUE && !isNaN(score))

  // If all values are N/A, return null
  if (validProbability.length === 0 || validImpact.length === 0) {
    return { P: null, I: null, risk: null, level: null }
  }

  // Calculate P: average of probability factors, rounded
  const sumP = validProbability.reduce((sum, score) => sum + score, 0)
  const P = Math.round(sumP / validProbability.length)

  // Calculate I: max of impact factors
  const I = Math.max(...validImpact)

  // Calculate risk
  const risk = P * I

  // Determine level
  const level = getRiskLevel(risk)

  return { P, I, risk, level }
}
